import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatScientificToDecimal,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  switchACMG,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
  setOpen: any
  setHistoryOpen: any
  setCommentsOpen: any
  setTranscript_list: any
  handleMenuClick: any
  getColumnSearchProps: any
  setFilterGeneOpen: any
  setMenuType: any
  setFilterGeneData: any
  getFilterGene: any
  setIndexOpen: any
  setGeneIndexOpen: any
  setGeneOpen: any
  setAcmgOpen: any
  setOMIMOpen: any
  setIgvOpen: any
  setIgvLocus: any
  setCnvScipOpen: any
  setCnvAllOpen: any
  VariationGeneRef: any
  analysisInfo: any
  query: any
  indexChecked: boolean
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1MT = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query
  const user = JSON.parse(localStorage.getItem('user') || '{}')

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    let column: ColumnDef<Variation>[] = [
      {
        accessorKey: 'gene',
        id: 'gene',
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            {/* <Filters keys="gene" /> */}

            <Stack maxWidth={'100%'}>
              <span
                style={{
                  lineHeight: 'normal',
                  textAlign: 'left'
                }}
              >
                {t('Gene')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('TranscriptID')}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          const badges = row.original.user_variant_comments
            ? row.original.user_variant_comments.comments.length
            : 0

          const hisBadges = row.original.tempHistory
            ? row.original.tempHistory.length
            : 0

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'
          const NM = `${row.original.transcript}(MANE)`

          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent="space-between"
                width={'100%'}
              >
                {ability && ability.can('read', 'report-search') ? (
                  <Badges
                    onClick={(e) => {
                      props.setHistoryOpen(true)

                      props.setCurrent(row.original)
                    }}
                    badgeContent={hisBadges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-45px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    <Badges
                      onClick={(e) => {
                        props.setCommentsOpen(true)

                        props.setCurrent(row.original)
                      }}
                      showZero
                      badgeContent={badges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': {
                          top: '6px',
                          right: '-18px'
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      {props.query?.title === t('MT') &&
                      row.original.MT_genes &&
                      Array.isArray(row.original.MT_genes) ? (
                        <Tooltip title={row.original.MT_genes.join(';')}>
                          <TypographyA
                            sx={{
                              color:
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : bgcolor === 'rgb(107,148,200,0.8)' ||
                                    bgcolor === 'rgb(109,56,149,0.8)'
                                  ? 'black'
                                  : '#23527c',
                              fontWeight: '700',
                              cursor: 'pointer',
                              maxWidth: 80,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {row.original.MT_genes.join(';')}
                          </TypographyA>
                        </Tooltip>
                      ) : (
                        <TypographyA
                          sx={{
                            color:
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgb(109,56,149,0.8)'
                                ? 'black'
                                : '#23527c',
                            fontWeight: '700'
                          }}
                        >
                          {row.original.gene}
                        </TypographyA>
                      )}
                    </Badges>
                  </Badges>
                ) : (
                  <Badges
                    onClick={(e) => {
                      props.setCommentsOpen(true)

                      props.setCurrent(row.original)
                    }}
                    showZero
                    badgeContent={badges}
                    color="primary"
                    sx={{
                      cursor: 'pointer',
                      color: 'inherit',
                      '& .MuiBadge-badge': {
                        top: '6px',
                        right: '-18px'
                      }
                    }}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right'
                    }}
                  >
                    {props.query?.title === t('MT') &&
                    row.original.MT_genes &&
                    Array.isArray(row.original.MT_genes) ? (
                      <Tooltip title={row.original.MT_genes.join(';')}>
                        <TypographyA
                          sx={{
                            color:
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgb(109,56,149,0.8)'
                                ? 'black'
                                : '#23527c',
                            fontWeight: '700',
                            cursor: 'pointer',
                            maxWidth: 80,
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {row.original.MT_genes.join(';')}
                        </TypographyA>
                      </Tooltip>
                    ) : (
                      <TypographyA
                        sx={{
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          fontWeight: '700'
                        }}
                      >
                        {row.original.gene}
                      </TypographyA>
                    )}
                  </Badges>
                )}

                {row && row.getCanExpand && row.getCanExpand() ? (
                  <button
                    {...{
                      onClick: row.getToggleExpandedHandler(),
                      style: { cursor: 'pointer' }
                    }}
                  >
                    {row.getIsExpanded() ? (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronDown />
                      </IconButton>
                    ) : (
                      <IconButton
                        sx={{
                          '& .MuiSvgIcon-root': {
                            color: 'black'
                          },
                          padding: 0
                        }}
                      >
                        <ChevronRight />
                      </IconButton>
                    )}
                  </button>
                ) : null}
              </Stack>

              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'100%'}
                direction="row"
                alignItems="center"
              >
                <Tooltip
                  title={`${row.original.transcript}(MANE)`}
                  placement={'top-start'}
                  disableHoverListener={NM.length <= 14}
                >
                  <TypographyA
                    id="qw"
                    sx={{
                      width: '100%',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : bgcolor === 'rgb(107,148,200,0.8)' ||
                            bgcolor === 'rgb(109,56,149,0.8)'
                          ? 'black'
                          : '#23527c',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.nuccore}/${row.original.transcript}`
                      )
                    }}
                  >
                    {NM.length >= 14 ? `${NM}` : NM}
                  </TypographyA>
                </Tooltip>

                {/* <IconButton
                  onClick={(e) => {
                    props.setTranscript_list(row.original.transcript_list)

                    props.handleMenuClick(e, 'list')
                  }}
                  sx={{
                    color: 'inherit',
                    padding: 0

                    // position: 'absolute',
                    // right: 0,
                    // bottom: '-1rem'
                  }}
                  size="small"
                >
                  <MenuDown />
                </IconButton> */}
              </Stack>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      },
      {
        accessorKey: 'dbSNP',
        id: 'dbSNP',
        header: () => (
          <Stack maxWidth="100%">
            <Stack maxWidth="100%">
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Position')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('dbSNP')}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        enableSorting: false,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <TypographyA
                sx={{
                  cursor: 'pointer',
                  color:
                    bgcolor === 'rgba(229,38,35,0.8)'
                      ? 'white'
                      : bgcolor === 'rgb(107,148,200,0.8)' ||
                        bgcolor === 'rgb(109,56,149,0.8)'
                      ? 'black'
                      : '#23527c',
                  fontWeight: '700'
                }}
                onClick={(e) => {
                  if (
                    (props.analysisInfo && props.analysisInfo.bamFile) ||
                    props.analysisInfo.sampleType === 'fastq'
                  ) {
                    props.handleMenuClick(e, 'IGV')

                    props.setCurrent(row.original)
                  } else if (
                    props.analysisInfo.analysisType === 'family_analysis' ||
                    props.analysisInfo.analysisType === 'big_family' ||
                    props.analysisInfo.analysisType === 'carrier'
                  ) {
                    const fqSamples = props.analysisInfo.samples?.filter(
                      (item: any) => item && item.sampleType === 'fastq'
                    )

                    if (fqSamples && fqSamples.length) {
                      props.handleMenuClick(e, 'IGV')

                      props.setCurrent(row.original)
                    } else {
                      window.open(
                        `${ExternalLinks.genome}?db=${
                          props.analysisInfo.genomeBuild ?? 'hg19'
                        }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                          row.original.chrom
                        }:${row.original.pos}-${row.original.pos}`
                      )
                    }
                  } else
                    window.open(
                      `${ExternalLinks.genome}?db=${
                        props.analysisInfo.genomeBuild ?? 'hg19'
                      }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                        row.original.chrom
                      }:${row.original.pos}-${row.original.pos}`
                    )
                }}
              >
                {`${row.original.chrom}:${row.original.pos}`}
              </TypographyA>

              <Tooltip
                disableHoverListener={row.original.rs_id?.length <= 14}
                title={row.original.rs_id}
                placement={'top-start'}
              >
                <TypographyA
                  sx={{
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)'
                        ? 'white'
                        : bgcolor === 'rgb(107,148,200,0.8)' ||
                          bgcolor === 'rgb(109,56,149,0.8)'
                        ? 'black'
                        : '#23527c',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontWeight: '700'
                  }}
                  onClick={() => {
                    window.open(
                      `${ExternalLinks.ncbi.snp_ref}?type=rs&rs=${row.original.rs_id}`
                    )
                  }}
                >
                  {`${row.original.rs_id}`}
                </TypographyA>
              </Tooltip>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'coding_effect',
        id: 'coding_effect',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Function')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {`${t('Location')}`}
              </span>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column" spacing={1}>
            <Tooltip
              disableHoverListener={row.original.coding_effect?.length <= 14}
              placement={'top-start'}
              title={row.original.coding_effect}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${row.original.coding_effect}`}</TypographyA>
            </Tooltip>

            <Tooltip
              disableHoverListener={
                row.original.var_location?.length + row.original.exon?.length <=
                14
              }
              placement={'top-start'}
              title={`${row.original.var_location}${
                row.original.exon === '.' ? '' : `(${row.original.exon})`
              }`}
            >
              <TypographyA
                sx={{
                  maxWidth: 100,
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis'
                }}
              >{`${row.original.var_location}${
                row.original.exon === '.' ? '' : `(${row.original.exon})`
              }`}</TypographyA>
            </Tooltip>
          </Stack>
        )
      },
      {
        accessorKey: 'nuc_change',
        id: 'nuc_change',
        enableSorting: false,
        header: () => (
          <React.Fragment>
            <Stack maxWidth="100%">
              <span
                style={{
                  textAlign: 'start'
                }}
              >
                {t('Base change')}
              </span>
            </Stack>
          </React.Fragment>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const sampleList = props.analysisInfo.samples ?? []
          const flag =
            sampleList?.some(
              (sample: any) =>
                (sample.cnvBatchTaskId &&
                  sample.cnvBatchStatus === 'processed') ||
                sample.hasExomeDepthCnv
            ) && row.original.CNV

          return (
            <Stack
              maxWidth={'100%'}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              alignItems={'center'}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems={'center'}
                  justifyContent="start"
                  width={'100%'}
                >
                  <Tooltip
                    disableHoverListener={row.original.nuc_change?.length <= 14}
                    title={`${row.original.nuc_change}`}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        color: '#23527c',
                        cursor: 'pointer'
                      }}
                      onClick={(e) => {
                        props.setCurrent(row.original)

                        props.setFilterGeneOpen(true)

                        props.setMenuType('filter_gene')

                        props.setFilterGeneData([])

                        props.getFilterGene.mutateAsync(row.original.gene)

                        e.stopPropagation()

                        e.preventDefault()
                      }}
                    >
                      {`${row.original.nuc_change}`}
                    </TypographyA>
                  </Tooltip>

                  {flag ? (
                    <TypographyA
                      sx={{
                        marginLeft: '15px',
                        color: 'red !important'
                      }}
                    >
                      cnv
                    </TypographyA>
                  ) : (
                    ''
                  )}
                </Stack>

                <Tooltip
                  disableHoverListener={row.original.c_nomen?.length <= 14}
                  title={`${row.original.c_nomen}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {`${row.original.c_nomen}`}
                  </TypographyA>
                </Tooltip>

                <Tooltip
                  disableHoverListener={row.original.p_nomen?.length <= 14}
                  title={`${row.original.p_nomen}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {`${row.original.p_nomen}`}
                  </TypographyA>
                </Tooltip>
              </Box>

              {/* <Box>
            <IconButton
              onClick={() => {
                props.setFilterGeneOpen(true)

                props.setMenuType('filter_gene')

                props.setFilterGeneData([])

                props.getFilterGene.mutateAsync(row.original.gene)
              }}
              aria-label="delete"
              size="small"
            >
              <Notes />
            </IconButton>
          </Box> */}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'coverage',
        id: 'coverage',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Tooltip
              placement="top-start"
              title={`实心圆表纯合，半实心表杂合；ref/alt`}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('Zygosity')}/${t('Coverage')}`}
                </span>

                {type === 'sample' ? (
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('QUAL')}
                    </span>
                  </React.Fragment>
                ) : (
                  ''
                )}
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              {type === 'sample' ? (
                <React.Fragment>
                  {(row.original.allele_frequency ||
                    row.original.allele_frequency === 0) &&
                  row.original.allele_frequency !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {row.original.allele_frequency < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA
                        sx={{
                          maxWidth: '100%'
                        }}
                      >
                        {`${getValue<string>()}  [${row.original.allele_frequency?.toFixed(
                          2
                        )}] `}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  <TypographyA
                    sx={{
                      maxWidth: '100%'
                    }}
                  >
                    {(row.original.QUAL as unknown as string) === '.'
                      ? '.'
                      : `${row.original.QUAL?.toFixed(1)}`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  {row.original.mom_id !== '.' &&
                  row.original.mom_allele_frequency &&
                  row.original.mom_allele_frequency !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {row.original.mom_allele_frequency < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA
                        sx={{
                          maxWidth: '100%'
                        }}
                      >
                        {`${
                          row.original.mom_coverage
                        } [${row.original.mom_allele_frequency.toFixed(
                          2
                        )}] (M)`}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  {row.original.dad_id !== '.' &&
                  row.original.dad_allele_frequency &&
                  row.original.dad_allele_frequency !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {row.original.dad_allele_frequency < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA
                        sx={{
                          maxWidth: '100%'
                        }}
                      >
                        {`${
                          row.original.dad_coverage
                        } [${row.original.dad_allele_frequency.toFixed(
                          2
                        )}] (F)`}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  {props.analysisInfo.analysisType === 'family_analysis' &&
                  (row.original.allele_frequency ||
                    row.original.allele_frequency === 0) &&
                  row.original.allele_frequency !== '.' ? (
                    <Stack direction={'row'} alignItems="center" spacing={1}>
                      <Stack maxWidth={'100%'} direction="column" spacing={1}>
                        {row.original.allele_frequency < 0.85 ? (
                          <CircleHalfFull
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        ) : (
                          <Circle
                            sx={{ color: 'black', fontSize: '0.875rem' }}
                          />
                        )}
                      </Stack>

                      <TypographyA
                        sx={{
                          maxWidth: '100%'
                        }}
                      >
                        {`${
                          row.original.coverage
                        } [${row.original.allele_frequency?.toFixed(2)}] (P)`}
                      </TypographyA>
                    </Stack>
                  ) : (
                    ''
                  )}

                  {row.original.members_data
                    ? row.original.members_data
                        .map((item) => {
                          const temp = renameChildTypes(
                            props.analysisInfo.samples
                          ).find((items: any) => items.id == item.sample_id)

                          return {
                            ...item,
                            type: temp ? temp.type : ''
                          }
                        })
                        .map((item: any) => {
                          if (item && item.allele_frequency && item.coverage) {
                            const temp = item

                            return (
                              <Tooltip
                                title={
                                  temp
                                    ? temp.type.includes('//')
                                      ? `${t(temp.type.split('//')[0])}${
                                          temp.type.split('//')[1]
                                        }`
                                      : t(temp.type)
                                    : ''
                                }
                                placement="top-end"
                              >
                                <Stack
                                  direction={'row'}
                                  alignItems="center"
                                  key={item.sample_id}
                                  spacing={1}
                                >
                                  <Stack
                                    maxWidth={'100%'}
                                    direction="column"
                                    spacing={1}
                                  >
                                    {item.allele_frequency < 0.85 ? (
                                      <CircleHalfFull
                                        sx={{
                                          color: 'black',
                                          fontSize: '0.875rem'
                                        }}
                                      />
                                    ) : (
                                      <Circle
                                        sx={{
                                          color: 'black',
                                          fontSize: '0.875rem'
                                        }}
                                      />
                                    )}
                                  </Stack>

                                  <TypographyA
                                    sx={{
                                      maxWidth: '100%'
                                    }}
                                  >
                                    {`${
                                      item.coverage
                                    } [${item.allele_frequency.toFixed(2)}] (${
                                      temp
                                        ? judgeType(
                                            temp.type,
                                            row.original.members_data
                                          )
                                        : ''
                                    })`}
                                  </TypographyA>
                                </Stack>
                              </Tooltip>
                            )
                          }
                        })
                    : ''}

                  {row.original.samples_info
                    ? row.original.samples_info
                        .map((item) => {
                          const temp = renameChildTypes(
                            props.analysisInfo.samples
                          ).find((items: any) => items.id == item.sample_id)

                          return {
                            ...item,
                            type: temp ? temp.type : ''
                          }
                        })
                        .map((item: any) => {
                          if (item && item.allele_frequency && item.coverage) {
                            const temp = item

                            return (
                              <Tooltip
                                title={
                                  temp
                                    ? temp.type.includes('//')
                                      ? `${t(temp.type.split('//')[0])}${
                                          temp.type.split('//')[1]
                                        }`
                                      : t(temp.type)
                                    : ''
                                }
                                placement="top-end"
                              >
                                <Stack
                                  direction={'row'}
                                  alignItems="center"
                                  key={item.sample_id}
                                  spacing={1}
                                >
                                  <Stack
                                    maxWidth={'100%'}
                                    direction="column"
                                    spacing={1}
                                  >
                                    {item.allele_frequency < 0.85 ? (
                                      <CircleHalfFull
                                        sx={{
                                          color: 'black',
                                          fontSize: '0.875rem'
                                        }}
                                      />
                                    ) : (
                                      <Circle
                                        sx={{
                                          color: 'black',
                                          fontSize: '0.875rem'
                                        }}
                                      />
                                    )}
                                  </Stack>

                                  <TypographyA
                                    sx={{
                                      maxWidth: '100%'
                                    }}
                                  >
                                    {`${
                                      item.coverage
                                    } [${item.allele_frequency.toFixed(2)}] (${
                                      temp
                                        ? judgeType(
                                            temp.type,
                                            row.original.samples_info
                                          )
                                        : ''
                                    })`}
                                  </TypographyA>
                                </Stack>
                              </Tooltip>
                            )
                          }
                        })
                    : ''}
                </React.Fragment>
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'index',
        id: 'index',
        enableSorting: false,
        header: () => (
          <Tooltip
            title={
              user && user.organization && user.organization.isShared ? (
                <>
                  <p>组织下有该基因的样本量/平台有该基因的样本量</p>

                  <p>组织下有该变异的样本量/组织下的总样本量</p>

                  <p>平台上有该变异的样本量/平台上的总样本量</p>
                </>
              ) : (
                <>
                  <p>账户下有该基因的样本量/平台有该基因的样本量</p>

                  <p>账户下有该变异的样本量/账户下的总样本量</p>

                  <p>平台上有该变异的样本量/平台上的总样本量</p>
                </>
              )
            }
          >
            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Gene Index')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (local)')}
                  </span>
                </React.Fragment>
              </Stack>

              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Variant Index (GLS)')}
                  </span>
                </React.Fragment>
              </Stack>
            </Stack>
          </Tooltip>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const Index = row.original.Index
          const GeneIndex = row.original.GeneIndex

          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              {GeneIndex ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setGeneIndexOpen(true)
                    }}
                  >
                    {`${GeneIndex.accountTotal} / ${GeneIndex.platformTotal}`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                ''
              )}

              {Index ? (
                <React.Fragment>
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setIndexOpen(true)
                    }}
                  >
                    {user && user.organization && user.organization.isShared
                      ? `${row.original.Index.organizationTotal} / ${row.original.Index.organizationTotalSamples}`
                      : `${row.original.Index.accountTotal} / ${row.original.Index.userTotalSamples}`}
                  </TypographyA>

                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                    onClick={() => {
                      props.setCurrent(row.original)

                      props.setIndexOpen(true)
                    }}
                  >
                    {`${row.original.Index.platformTotal} / ${
                      row.original.Index.glsTotalSamples +
                      row.original.Index.btgTotalSamples
                    }`}
                  </TypographyA>
                </React.Fragment>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'articles',
        id: 'articles',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <React.Fragment>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Articles')}
                </span>
              </React.Fragment>
            </Stack>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const LitVarArticles = row.original.LitVarArticles
          const Articles = row.original.Articles
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack
              onClick={(e) => {
                //
                e.stopPropagation()

                props.setAcmgOpen(true)

                props.setCurrent(row.original)

                setTimeout(() => {
                  props.VariationGeneRef.current &&
                    props.VariationGeneRef.current.handleClickLeft &&
                    props.VariationGeneRef.current.handleClickLeft(
                      3,
                      'Publication'
                    )
                }, 2000)
              }}
              maxWidth={'100%'}
              direction="column"
              spacing={1}
              sx={{
                cursor: 'pointer'
              }}
            >
              {LitVarArticles?.count ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  LitVar:{LitVarArticles?.count ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.variantPubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  Clinvar:{Articles?.variantPubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}

              {Articles?.genePubmedArticlesCount ? (
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                  }}
                >
                  OMIM:{Articles?.genePubmedArticlesCount ?? 0}
                </TypographyA>
              ) : (
                ''
              )}
            </Stack>
          )
        }
      },
      {
        accessorKey: 'gnomAD_exome_ALL',
        id: 'gnomAD_exome_ALL',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            {/* <Filters keys="gnomAD_exome_ALL" /> */}

            <Stack maxWidth={'100%'}>
              {props.query?.title === t('MT') ? (
                <>
                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_hom_all`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`gAD_het_all`}</span>
                </>
              ) : (
                <>
                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`${t('gAD')}_ALL`}</span>

                  <span
                    style={{ lineHeight: 'normal', textAlign: 'left' }}
                  >{`${t('gAD')}_EAS`}</span>
                </>
              )}
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column">
            {props.query?.title === t('MT') ? (
              <>
                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {row.original.AF_hom === ('.' as unknown as string)
                    ? '.'
                    : Number(row.original.AF_hom) === 0
                    ? 0
                    : `${Number(row.original.AF_hom)?.toFixed(8)}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {(row.original.AF_het as unknown as string) === '.'
                    ? '.'
                    : Number(row.original.AF_het) === 0
                    ? 0
                    : `${Number(row.original.AF_het)?.toFixed(8)}`}
                </TypographyA>
              </>
            ) : (
              <>
                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {getValue<number | string>() === '.'
                    ? '.'
                    : getValue<number>() === 0
                    ? 0
                    : `${getValue<number>()?.toFixed(8)}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {(row.original.gnomAD_exome_EAS as unknown as string) === '.'
                    ? '.'
                    : row.original.gnomAD_exome_EAS === 0
                    ? 0
                    : `${row.original.gnomAD_exome_EAS?.toFixed(8)}`}
                </TypographyA>
              </>
            )}
          </Stack>
        )
      },
      {
        accessorKey: 'mitomap_freq',
        id: 'mitomap_freq',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {`${t('mitomap_freq')}`}
              </span>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => (
          <Stack maxWidth={'100%'} direction="column">
            <TypographyA
              sx={{
                maxWidth: '100%'
              }}
            >
              {(row.original.mitomap_freq as unknown as string) === '.'
                ? '.'
                : row.original.mitomap_freq === 0
                ? 0
                : `${row.original.mitomap_freq?.toFixed(4)}`}
            </TypographyA>
          </Stack>
        )
      },
      {
        accessorKey: 'f_score',
        id: 'F_score',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            {/* <Filters keys="f_score" /> */}

            <Tooltip
              placement="top-start"
              title="F score:GLSofort 软件AI模型综合评分：致病性、质量、表型、遗传模式、文献等等，最可能的致病位点"
            >
              <Stack maxWidth={'100%'}>
                <span
                  style={{
                    lineHeight: 'normal',
                    textAlign: 'left'
                  }}
                >
                  {t('F')}
                </span>

                <span
                  style={{
                    lineHeight: 'normal',
                    textAlign: 'left'
                  }}
                >
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <React.Fragment>
                    <TypographyA
                      onClick={(e) => {
                        e.stopPropagation()

                        props.setGeneOpen(true)

                        props.setCurrent(row.original)
                      }}
                      sx={{
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : bgcolor === 'rgb(107,148,200,0.8)' ||
                              bgcolor === 'rgb(109,56,149,0.8)'
                            ? 'black'
                            : '#23527c',
                        textAlign: 'center',
                        fontWeight: '700'
                      }}
                    >
                      {`${
                        (row.original.f_score as any) !== '.'
                          ? (row.original.f_score * 100)?.toFixed(2)
                          : '.'
                      }`}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {row.original.mom_id !== '.' && row.original.mom_fscore ? (
                      <TypographyA
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                        onClick={(e) => {
                          e.stopPropagation()

                          props.setGeneOpen(true)

                          props.setCurrent(row.original)
                        }}
                      >
                        {`${
                          (row.original.mom_fscore as any) !== '.'
                            ? (row.original.mom_fscore * 100)?.toFixed(2)
                            : '.'
                        }(M)`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {row.original.dad_id !== '.' && row.original.dad_fscore ? (
                      <TypographyA
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                        onClick={(e) => {
                          e.stopPropagation()

                          props.setGeneOpen(true)

                          props.setCurrent(row.original)
                        }}
                      >
                        {`${
                          (row.original.dad_fscore as any) !== '.' &&
                          row.original.dad_fscore
                            ? (row.original.dad_fscore * 100)?.toFixed(2)
                            : '.'
                        }(F)`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {props.analysisInfo.analysisType !== 'carrier' &&
                    row.original.f_score ? (
                      <TypographyA
                        onClick={(e) => {
                          e.stopPropagation()

                          props.setGeneOpen(true)

                          props.setCurrent(row.original)
                        }}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.f_score as any) !== '.' &&
                          row.original.f_score
                            ? `${(row.original.f_score * 100)?.toFixed(2)}(P)`
                            : '.'
                        }`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {row.original.members_data
                      ? row.original.members_data
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)

                            return {
                              ...item,
                              type: temp ? temp.type : ''
                            }
                          })
                          .map((item: any) => {
                            if (item && item.f_score) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    onClick={(e) => {
                                      e.stopPropagation()

                                      props.setGeneOpen(true)

                                      props.setCurrent(row.original)
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      color:
                                        bgcolor === 'rgba(229,38,35,0.8)'
                                          ? 'white'
                                          : bgcolor ===
                                              'rgb(107,148,200,0.8)' ||
                                            bgcolor === 'rgb(109,56,149,0.8)'
                                          ? 'black'
                                          : '#23527c',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.f_score as any) !== '.'
                                        ? `${(item.f_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.members_data
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}

                    {row.original.samples_info
                      ? row.original.samples_info
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)

                            return {
                              ...item,
                              type: temp ? temp.type : ''
                            }
                          })
                          .map((item) => {
                            if (item && (item.f_score || item.f_score === 0)) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    onClick={(e) => {
                                      e.stopPropagation()

                                      props.setGeneOpen(true)

                                      props.setCurrent(row.original)
                                    }}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.8rem',
                                      color:
                                        bgcolor === 'rgba(229,38,35,0.8)'
                                          ? 'white'
                                          : bgcolor ===
                                              'rgb(107,148,200,0.8)' ||
                                            bgcolor === 'rgb(109,56,149,0.8)'
                                          ? 'black'
                                          : '#23527c',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.f_score as any) !== '.'
                                        ? `${(item.f_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.samples_info
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}
                  </React.Fragment>
                )}
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'g_score',
        id: 'G_score',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            {/* <Filters keys="g_score" /> */}

            <Tooltip
              placement="top-start"
              title={t(
                'G score: Phenotypic correlation score, which is a comprehensive score associated with genetic diseases and other genetic diseases that interact with genes'
              )}
            >
              <Stack maxWidth={'100%'}>
                <span
                  style={{
                    lineHeight: 'normal',
                    textAlign: 'left'
                  }}
                >
                  {t('G')}
                </span>

                <span
                  style={{
                    lineHeight: 'normal',
                    textAlign: 'left'
                  }}
                >
                  Score
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <React.Fragment>
                    <TypographyA
                      sx={{
                        fontSize: '0.8rem',
                        textAlign: 'center',
                        fontWeight: '700'
                      }}
                    >
                      {`${
                        (row.original.g_score as any) !== '.'
                          ? (row.original.g_score * 100)?.toFixed(2)
                          : '.'
                      }`}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {row.original.mom_id !== '.' &&
                    (row.original.mom_gscore ||
                      row.original.mom_gscore === 0) ? (
                      <TypographyA
                        sx={{
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.mom_gscore as any) !== '.'
                            ? (row.original.mom_gscore * 100)?.toFixed(2)
                            : '.'
                        }(M)`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {row.original.dad_id !== '.' &&
                    (row.original.dad_gscore ||
                      row.original.dad_gscore === 0) ? (
                      <TypographyA
                        sx={{
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.dad_gscore as any) !== '.'
                            ? (row.original.dad_gscore * 100)?.toFixed(2)
                            : '.'
                        }(F)`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {(props.analysisInfo.analysisType !== 'carrier' &&
                      row.original.g_score) ||
                    row.original.g_score === 0 ? (
                      <TypographyA
                        sx={{
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.g_score as any) !== '.'
                            ? `${(row.original.g_score * 100)?.toFixed(2)}(P)`
                            : '.'
                        }`}
                      </TypographyA>
                    ) : (
                      ''
                    )}

                    {row.original.members_data
                      ? row.original.members_data
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)

                            return {
                              ...item,
                              type: temp ? temp.type : ''
                            }
                          })
                          .map((item: any) => {
                            if (item && item.g_score) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    sx={{
                                      fontSize: '0.8rem',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.g_score as any) !== '.'
                                        ? `${(item.g_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.members_data
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}

                    {row.original.samples_info
                      ? row.original.samples_info
                          .map((item) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)

                            return {
                              ...item,
                              type: temp ? temp.type : ''
                            }
                          })
                          .map((item: any) => {
                            if (item && (item.g_score || item.g_score === 0)) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <TypographyA
                                    sx={{
                                      fontSize: '0.8rem',
                                      textAlign: 'center',
                                      fontWeight: '700'
                                    }}
                                  >
                                    {`${
                                      (item.g_score as any) !== '.'
                                        ? `${(item.g_score * 100)?.toFixed(
                                            2
                                          )}(${
                                            temp
                                              ? judgeType(
                                                  temp.type,
                                                  row.original.samples_info
                                                )
                                              : ''
                                          })`
                                        : '.'
                                    }`}
                                  </TypographyA>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}
                  </React.Fragment>
                )}
              </Stack>
            </Stack>
          )
        }
      },
      {
        accessorKey: 'SpliceAI_max_score',
        id: 'spliceAI_max_score',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} alignItems="center">
            <Tooltip
              title={`D(${t('harmful')}) U(${t('unknown significance')}) B(${t(
                'benign'
              )})`}
              placement="top-start"
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('SpliceAI')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Revel')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('AlphaM')}
                </span>
              </Stack>
            </Tooltip>
          </Stack>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          return (
            <Stack maxWidth={'100%'}>
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.SpliceAI_max_score as unknown as string) ===
                    '.'
                      ? '.'
                      : `${row.original.SpliceAI_max_score?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        row.original.SpliceAI_max_score >= 0.2
                          ? 'bold'
                          : 'normal'
                    }}
                  >
                    {`${
                      row.original.SpliceAI_max_score >= 0.2
                        ? 'D'
                        : row.original.SpliceAI_max_score <= 0.1
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.REVEL_score as unknown as string) === '.'
                      ? '.'
                      : `${row.original.REVEL_score?.toFixed(3)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        row.original.REVEL_score >= 0.644 ? 'bold' : 'normal'
                    }}
                  >
                    {`${
                      row.original.REVEL_score >= 0.644
                        ? 'D'
                        : row.original.REVEL_score <= 0.29
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>

                <TypographyA
                  sx={{
                    fontSize: '0.8rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pr: '10px'
                  }}
                >
                  <span>
                    {(row.original.AM_pathogenicity as unknown as string) ===
                      '.' || !row.original.AM_pathogenicity
                      ? '.'
                      : `${Number(row.original.AM_pathogenicity)?.toFixed(2)}`}
                  </span>

                  <span
                    style={{
                      fontWeight:
                        Number(row.original.AM_pathogenicity) >= 0.564
                          ? 'bold'
                          : 'normal'
                    }}
                  >
                    {`${
                      Number(row.original.AM_pathogenicity) >= 0.564
                        ? 'D'
                        : Number(row.original.AM_pathogenicity) <= 0.34
                        ? ''
                        : ''
                    }`}
                  </span>
                </TypographyA>
              </Stack>
            </Stack>
          )
        }
      },

      // {
      //   accessorKey: 'Revel_score',
      //   id: 'revel_score',
      //   header: () => (
      //     <React.Fragment>
      //       <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
      //         <span>{t('Revel')}</span>
      //       </Stack>
      //     </React.Fragment>
      //   ),
      //   footer: (props) => props.column.id,
      //   cell: ({ row, getValue }) => {
      //     return (
      //       <Stack maxWidth={'100%'}>
      //         <TypographyA
      //           sx={{
      //             maxWidth: '100%',
      //             textAlign: 'center'
      //           }}
      //         >
      //           {(row.original.REVEL_score as unknown as string) === '.'
      //             ? '.'
      //             : `${row.original.REVEL_score?.toFixed(3)}`}
      //         </TypographyA>
      //       </Stack>
      //     )
      //   }
      // },
      // {
      //   accessorKey: 'pathogenicity',
      //   id: 'Pathogenicity',
      //   header: () => (
      //     <React.Fragment>
      //       <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
      //         <span>{t('AlphaM')}</span>
      //       </Stack>
      //     </React.Fragment>
      //   ),
      //   footer: (props) => props.column.id,
      //   cell: ({ row, getValue }) => {
      //     return (
      //       <Stack maxWidth={'100%'}>
      //         <TypographyA
      //           sx={{
      //             maxWidth: '100%',
      //             textAlign: 'center'
      //           }}
      //         >
      //           {(row.original.AM_pathogenicity as unknown as string) === '.' ||
      //           !row.original.AM_pathogenicity
      //             ? '.'
      //             : `${Number(row.original.AM_pathogenicity)?.toFixed(2)}`}
      //         </TypographyA>
      //       </Stack>
      //     )
      //   }
      // },
      {
        accessorKey: 'CLINVAR_variant_id',
        id: 'cLINVAR_variant_id',
        header: () => (
          <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
            {/* <Filters keys="CLINVAR_clnsig_final" /> */}

            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Clinvar (star)')}
              </span>

              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {t('Curation')}
              </span>
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          let bgcolor = color.find(
            (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
          )
            ? (color.find(
                (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
              )?.color as any)
            : 'rgba(0, 0, 0)'

          // loss_of_function等于1 并且CLINSIG_final不等于P/LP
          if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
            //
          } else if (
            row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
          ) {
            //
          } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

          return (
            <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
              <Stack
                alignItems={'center'}
                maxWidth={'100%'}
                spacing={1}
                direction="row"
              >
                <Tooltip
                  title={row.original.CLINVAR_clnsig_final}
                  placement={'top-start'}
                >
                  <TypographyA
                    sx={{
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c',
                      cursor: 'pointer',
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.variation}/${getValue<string>()}`
                      )
                    }}
                  >
                    {`${row.original.CLINVAR_clnsig_final}`}
                  </TypographyA>
                </Tooltip>

                {row.original.CLINVAR_gold_stars &&
                row.original.CLINVAR_gold_stars !== '.' ? (
                  <Rating
                    readOnly
                    value={row.original.CLINVAR_gold_stars as unknown as number}
                    max={row.original.CLINVAR_gold_stars as unknown as number}
                  />
                ) : (
                  ''
                )}
              </Stack>

              <TypographyA
                sx={{
                  maxWidth: '100%'
                }}
              >
                {`${row.original.curation}`}
              </TypographyA>
            </Stack>
          )
        }
      },

      {
        accessorKey: 'ACMG_priority',
        id: 'ACMG_priority',
        header: () => (
          <React.Fragment>
            {/* <Filters keys="ACMG_classification" /> */}

            <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
              <span>{t('ACMG')}</span>

              <span>{t('Classification')}</span>
            </Stack>
          </React.Fragment>
        ),
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          const acmg = row.original.ACMG_classification
            ? `${row.original.ACMG_classification}`
            : row.original.user_acmg_classification
            ? `${row.original.user_acmg_classification}`
            : ''

          let bgcolor = color.find((i) => i.key === acmg?.toLowerCase())
            ? (color.find((i) => i.key === acmg?.toLowerCase())?.color as
                | 'rgb(240,239,240,0.4)'
                | 'rgba(221,109,32,0.8)'
                | 'rgb(255,242,202)'
                | 'rgb(245,182,193)')
            : 'none'
          if (row.original.user_acmg_classification) {
            bgcolor = color.find(
              (i) =>
                i.key === row.original.user_acmg_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key ===
                    row.original.user_acmg_classification?.toLowerCase()
                )?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        textAlign: 'center'
                      }}
                      label={`${row.original.user_acmg_classification
                        .split(' ')
                        .map((word) => {
                          if (word.includes('Uncertain')) return `VU`
                          if (word.includes('null')) return ``
                          else return word[0] && word[0].toUpperCase()
                        })
                        .join('')}${
                        row.original.ACMG_score || row.original.ACMG_score === 0
                          ? `:${row.original.ACMG_score}`
                          : ``
                      }`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)

                        props.setCurrent(row.original)
                      }}
                    />
                  </Box>

                  <Box>
                    {row.original.user_classification ? (
                      <Chip
                        key={row.original.user_classification}
                        size="small"
                        label={`${row.original.user_classification
                          .split(' ')
                          .map((word) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0] && word[0].toUpperCase()
                          })
                          .join('')}`}
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          minWidth: '45px'
                        }}
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                      />
                    ) : (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                        sx={{
                          padding: 0
                        }}
                      >
                        <CommentOutline
                          sx={{
                            fontSize: '1rem'
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            )
          } else if (
            acmg !== '.' ||
            row.original.user_acmg_classification !== '.'
          ) {
            return (
              <Stack maxWidth={'100%'}>
                <Box
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column'
                  }}
                >
                  <Box>
                    <Chip
                      sx={{
                        bgcolor: bgcolor,
                        color:
                          bgcolor === 'rgb(240,239,240,0.4)'
                            ? 'black'
                            : 'white',
                        justifyContent: 'center',
                        fontSize: '0.75rem',
                        minWidth: '45px',
                        fontWeight: '700',
                        textAlign: 'center'
                      }}
                      label={`${acmg
                        ?.split(' ')
                        .map((word) => {
                          if (word.includes('Uncertain')) return `VU`
                          if (word.includes('null')) return ``
                          else return word[0] && word[0].toUpperCase()
                        })
                        .join('')}${
                        row.original.ACMG_score || row.original.ACMG_score === 0
                          ? `:${row.original.ACMG_score}`
                          : ``
                      }`}
                      size="small"
                      onClick={() => {
                        props.setAcmgOpen(true)

                        props.setCurrent(row.original)
                      }}
                    />
                  </Box>

                  <Box>
                    {row.original.user_classification ? (
                      <Chip
                        key={row.original.user_classification}
                        size="small"
                        label={`${row.original.user_classification
                          .split(' ')
                          .map((word) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0] && word[0].toUpperCase()
                          })
                          .join('')}`}
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          fontWeight: 700,
                          textAlign: 'center',
                          minWidth: '45px'
                        }}
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                      />
                    ) : (
                      <IconButton
                        aria-label="edit"
                        onClick={() => {
                          props.setCurrent(row.original)

                          props.setOpen(true)
                        }}
                        sx={{
                          padding: 0
                        }}
                      >
                        <CommentOutline
                          sx={{
                            fontSize: '1rem'
                          }}
                        />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            )
          } else
            return (
              <>
                <TypographyA
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center'
                  }}
                >
                  {`${row.original.ACMG_score}`}
                </TypographyA>

                <TypographyA textAlign={'center'}>.</TypographyA>
              </>
            )
        }
      },
      {
        accessorKey: 'inheritance',
        id: 'inheritance',
        header: () => (
          <Stack width={'100%'} direction={'row'} justifyContent="center">
            <Stack maxWidth={'100%'} justifyContent="center">
              {props.query && props.query.key && props.query.key === '5' ? (
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Disease Information')}
                </span>
              ) : (
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('OMIM')}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Inheritance')}
                  </span>
                </React.Fragment>
              )}
            </Stack>
          </Stack>
        ),
        enableSorting: false,
        footer: (props) => props.column.id,
        cell: ({ row, getValue }) => {
          if (props.query && props.query.key && props.query.key === '5') {
            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {row.original.MitoMap && row.original.MitoMap.disease_ch ? (
                  <CustomWidthTooltip
                    title={
                      <Box>
                        <Typography color={'white'}>
                          {i18n && i18n.language === 'zh'
                            ? row.original.MitoMap.disease_ch
                            : row.original.MitoMap.disease}
                        </Typography>

                        <Typography color={'white'}>
                          {i18n && i18n.language === 'zh'
                            ? row.original.MitoMap.info
                            : ''}
                        </Typography>
                      </Box>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                  >
                    <Box>
                      <TypographyA
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          lineHeight: '24px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                          mb: '4px'
                        }}
                        key={`${row.original.MitoMap.disease_ch}`}
                      >
                        {i18n && i18n.language === 'zh'
                          ? row.original.MitoMap.disease_ch
                          : row.original.MitoMap.disease}
                      </TypographyA>

                      <TypographyA
                        sx={{
                          width: '100%',
                          maxWidth: 200,
                          lineHeight: '24px',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                          mb: '4px'
                        }}
                        key={`${row.original.MitoMap.info}`}
                      >
                        {i18n && i18n.language === 'zh'
                          ? row.original.MitoMap.info
                          : ''}
                      </TypographyA>
                    </Box>
                  </CustomWidthTooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          } else if (
            i18n &&
            i18n.language === 'zh' &&
            row.original.OMIM?.geneDiseaseTranslated &&
            row.original.OMIM?.geneDiseaseTranslated.data
          ) {
            let OMIMValue = row.original.OMIM?.geneDiseaseTranslated?.data

            OMIMValue = unique(OMIMValue, 'disease')

            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {OMIMValue && OMIMValue.length ? (
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        {OMIMValue.map((item: any, index: any) => (
                          <TypographyA
                            sx={{
                              fontSize: '0.85rem',
                              lineHeight: '1rem',
                              maxWidth: isWindow()
                                ? '400px !important'
                                : '200px !important'
                            }}
                            key={`${item.disease}_${index}`}
                          >
                            {`(${
                              item.genetic_mode ? item.genetic_mode : '.'
                            })-${
                              i18n && i18n.language === 'zh' ? item.disease : ''
                            }`}
                          </TypographyA>
                        ))}
                      </React.Fragment>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}

                    // enterDelay={1000}
                    // onMouseEnter={async (e) => {
                    //   const sourceLanguage = 'en',
                    //     targetLanguage = 'zh'
                    //   const params: TranslateDto = {
                    //     sourceLanguage,
                    //     targetLanguage,
                    //     sourceText: OMIMValue
                    //   }
                    //   Translate.mutateAsync(params)
                    // }}
                  >
                    <Box>
                      {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                        return (
                          <TypographyA
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              lineHeight: '24px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              color:
                                bgcolor === 'rgb(109,56,149,0.8)' ||
                                bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : '#23527c',
                              mb: '4px'
                            }}
                            onClick={() => {
                              props.setCurrent(row.original)

                              props.setOMIMOpen(true)
                            }}
                            key={`${items.disease}_${index}`}
                          >
                            <Chip
                              label={`${
                                items.genetic_mode ? items.genetic_mode : '.'
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.75rem',
                                fontWeight: '700'
                              }}
                            />

                            <span
                              style={{
                                lineHeight: '24px'
                              }}
                            >{`${
                              i18n && i18n.language === 'zh'
                                ? items.disease + '-'
                                : ''
                            }${items.disease}`}</span>
                          </TypographyA>
                        )
                      })}

                      {/* {OMIMValue.length > 3 ? '...' : ''} */}
                    </Box>
                  </HtmlTooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          } else {
            let OMIMValue =
              row.original.OMIM?.omimData.map((item) => {
                const inheritances: string[] = []

                if (
                  item &&
                  item.clinicalSynopsis &&
                  item.clinicalSynopsis.INHERITANCE &&
                  Array.isArray(item.clinicalSynopsis.INHERITANCE)
                ) {
                  item.clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                    inheritances.push(str.toLowerCase())
                  )
                }
                const inheritance = formatVariantInheritance(
                  Array.from([...new Set(inheritances)]).join(',')
                )

                return {
                  ...item,
                  inheritance
                }
              }) ?? []

            OMIMValue = unique(OMIMValue, 'phenoName')

            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} spacing={1}>
                {OMIMValue && OMIMValue.length ? (
                  <HtmlTooltip
                    title={
                      <React.Fragment>
                        {OMIMValue.map((item: any, index) => (
                          <TypographyA
                            sx={{
                              fontSize: '0.85rem',
                              lineHeight: '1rem',
                              maxWidth: isWindow()
                                ? '400px !important'
                                : '200px !important'
                            }}
                            key={`${item.phenoName}_${index}`}
                          >
                            {`(${item.inheritance ? item.inheritance : '.'})-${
                              i18n && i18n.language === 'zh'
                                ? item.pheno_name_cn + '-'
                                : ''
                            }${item.phenoName}`}
                          </TypographyA>
                        ))}
                      </React.Fragment>
                    }
                    placement={'top-start'}
                    TransitionComponent={Slide}
                    sx={{
                      '& .MuiTooltip-tooltip': {
                        maxWidth: isWindow() ? '400px' : '200px'
                      }
                    }}

                    // enterDelay={1000}
                    // onMouseEnter={async (e) => {
                    //   const sourceLanguage = 'en',
                    //     targetLanguage = 'zh'
                    //   const params: TranslateDto = {
                    //     sourceLanguage,
                    //     targetLanguage,
                    //     sourceText: OMIMValue
                    //   }
                    //   Translate.mutateAsync(params)
                    // }}
                  >
                    <Box>
                      {OMIMValue.slice(0, 2).map((items: any, index) => {
                        return (
                          <TypographyA
                            sx={{
                              width: '100%',
                              maxWidth: 200,
                              lineHeight: '24px',
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              cursor: 'pointer',
                              color:
                                bgcolor === 'rgb(109,56,149,0.8)' ||
                                bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : '#23527c',
                              mb: '4px'
                            }}
                            onClick={() => {
                              props.setCurrent(row.original)

                              props.setOMIMOpen(true)
                            }}
                            key={`${items.phenoMim}_${index}`}
                          >
                            <Chip
                              label={`${
                                items.inheritance ? items.inheritance : '.'
                              }`}
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: '0.75rem',
                                fontWeight: '700'
                              }}
                            />

                            <span
                              style={{
                                lineHeight: '24px'
                              }}
                            >{`${
                              i18n && i18n.language === 'zh'
                                ? items.pheno_name_cn + '-'
                                : ''
                            }${items.phenoName}`}</span>
                          </TypographyA>
                        )
                      })}

                      {/* {OMIMValue.length > 3 ? '...' : ''} */}
                    </Box>
                  </HtmlTooltip>
                ) : (
                  <TypographyA>.</TypographyA>
                )}
              </Stack>
            )
          }
        }
      },
      {
        accessorKey: 'status',
        id: 'status ',
        enableSorting: false,
        header: () => (
          <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
            <Stack maxWidth={'100%'}>
              <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                {`mitomap ${t('status')}`}
              </span>
            </Stack>
          </Stack>
        ),
        cell: ({ row, getValue }) => {
          return (
            <Stack
              maxWidth={'150px'}
              width={'100%'}
              marginTop={'12px'}
              direction="column"
              alignItems="start"
            >
              <HtmlTooltip
                placement={'top-start'}
                TransitionComponent={Slide}
                sx={{
                  '& .MuiTooltip-tooltip': {
                    maxWidth: isWindow() ? '400px' : '200px'
                  }
                }}
                title={row.original.MitoMap ? row.original.MitoMap?.status : ''}
              >
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 200,
                    lineHeight: '24px',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                    color: '#23527c',
                    mb: '4px'
                  }}
                >
                  {row.original.MitoMap ? row.original.MitoMap?.status : ''}
                </TypographyA>
              </HtmlTooltip>
            </Stack>
          )
        },
        footer: (props) => props.column.id
      }
    ]

    if (
      props.query &&
      props.query.title &&
      props.analysisInfo.pipelineVersion
    ) {
      column = [
        {
          accessorKey: 'gene',
          id: 'gene',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              {/* <Filters keys="gene" /> */}

              <Stack maxWidth={'100%'}>
                <span
                  style={{
                    lineHeight: 'normal',
                    textAlign: 'left'
                  }}
                >
                  {t('Gene')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('TranscriptID')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            const badges = row.original.user_variant_comments
              ? row.original.user_variant_comments.comments.length
              : 0

            const hisBadges = row.original.tempHistory
              ? row.original.tempHistory.length
              : 0

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            const NM = `${row.original.feature}(${row.original.mane ?? 'MANE'})`
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                direction="column"
                alignItems="start"
              >
                <Stack
                  direction="row"
                  alignItems={'center'}
                  justifyContent="space-between"
                  width={'100%'}
                >
                  {ability && ability.can('read', 'report-search') ? (
                    <Badges
                      onClick={(e) => {
                        e.stopPropagation()
                        props.setHistoryOpen(true)

                        props.setCurrent && props.setCurrent(row.original)
                      }}
                      badgeContent={hisBadges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': {
                          top: '6px',
                          right: '-45px'
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <Badges
                        onClick={(e) => {
                          e.stopPropagation()
                          props.setCommentsOpen(true)

                          props.setCurrent && props.setCurrent(row.original)
                        }}
                        showZero
                        badgeContent={badges}
                        color="primary"
                        sx={{
                          cursor: 'pointer',
                          color: 'inherit',
                          '& .MuiBadge-badge': {
                            top: '6px',
                            right: '-9px'
                          }
                        }}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right'
                        }}
                      >
                        {props.query?.title === t('MT') &&
                        row.original.MT_genes &&
                        Array.isArray(row.original.MT_genes) ? (
                          <Tooltip title={row.original.MT_genes.join(';')}>
                            <TypographyA
                              sx={{
                                color:
                                  bgcolor === 'rgba(229,38,35,0.8)'
                                    ? 'white'
                                    : bgcolor === 'rgb(107,148,200,0.8)' ||
                                      bgcolor === 'rgb(109,56,149,0.8)'
                                    ? 'black'
                                    : '#23527c',
                                fontWeight: '700',
                                cursor: 'pointer',
                                maxWidth: 80,
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis'
                              }}
                            >
                              {row.original.MT_genes.join(';')}
                            </TypographyA>
                          </Tooltip>
                        ) : (
                          <TypographyA
                            sx={{
                              color:
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : bgcolor === 'rgb(107,148,200,0.8)' ||
                                    bgcolor === 'rgb(109,56,149,0.8)'
                                  ? 'black'
                                  : '#23527c',
                              fontWeight: '700'
                            }}
                          >
                            {row.original.gene}
                          </TypographyA>
                        )}
                      </Badges>
                    </Badges>
                  ) : (
                    <Badges
                      onClick={(e) => {
                        e.stopPropagation()
                        props.setCommentsOpen(true)

                        props.setCurrent && props.setCurrent(row.original)
                      }}
                      showZero
                      badgeContent={badges}
                      color="primary"
                      sx={{
                        cursor: 'pointer',
                        color: 'inherit',
                        '& .MuiBadge-badge': {
                          top: '6px',
                          right: '-9px'
                        }
                      }}
                      anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      {props.query?.title === t('MT') &&
                      row.original.MT_genes &&
                      Array.isArray(row.original.MT_genes) ? (
                        <Tooltip title={row.original.MT_genes.join(';')}>
                          <TypographyA
                            sx={{
                              color:
                                bgcolor === 'rgba(229,38,35,0.8)'
                                  ? 'white'
                                  : bgcolor === 'rgb(107,148,200,0.8)' ||
                                    bgcolor === 'rgb(109,56,149,0.8)'
                                  ? 'black'
                                  : '#23527c',
                              fontWeight: '700',
                              cursor: 'pointer',
                              maxWidth: 80,
                              overflow: 'hidden',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {row.original.MT_genes.join(';')}
                          </TypographyA>
                        </Tooltip>
                      ) : (
                        <TypographyA
                          sx={{
                            color:
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgb(109,56,149,0.8)'
                                ? 'black'
                                : '#23527c',
                            fontWeight: '700'
                          }}
                        >
                          {row.original.gene}
                        </TypographyA>
                      )}
                    </Badges>
                  )}

                  {/* <Box>
                <IconButton
                  onClick={() => {
                    props.setFilterGeneOpen(true)

                    props.setMenuType('filter_gene')

                    props.setFilterGeneData([])

                    props.getFilterGene.mutateAsync(row.original.gene)
                  }}
                  aria-label="delete"
                  size="small"
                >
                  <Notes />
                </IconButton>
              </Box> */}
                </Stack>

                <Stack
                  sx={{
                    position: 'relative'
                  }}
                  maxWidth={'100%'}
                  direction="row"
                  alignItems="center"
                >
                  <Tooltip
                    title={NM}
                    placement={'top-start'}
                    disableHoverListener={NM.length <= 14}
                  >
                    <TypographyA
                      id="qw"
                      sx={{
                        width: '100%',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : bgcolor === 'rgb(107,148,200,0.8)' ||
                              bgcolor === 'rgb(109,56,149,0.8)'
                            ? 'black'
                            : '#23527c',
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        window.open(
                          `${ExternalLinks.ncbi.nuccore}/${row.original.feature}`
                        )
                      }}
                    >
                      {NM.length >= 14 ? `${NM}` : NM}
                    </TypographyA>
                  </Tooltip>

                  {row && row.getCanExpand && row.getCanExpand() ? (
                    <button
                      {...{
                        onClick: row.getToggleExpandedHandler(),
                        style: { cursor: 'pointer' }
                      }}
                    >
                      {row.getIsExpanded() ? (
                        <IconButton
                          sx={{
                            '& .MuiSvgIcon-root': {
                              color: 'black'
                            },
                            padding: 0
                          }}
                        >
                          <ChevronDown />
                        </IconButton>
                      ) : (
                        <IconButton
                          sx={{
                            '& .MuiSvgIcon-root': {
                              color: 'black'
                            },
                            padding: 0
                          }}
                        >
                          <ChevronRight />
                        </IconButton>
                      )}
                    </button>
                  ) : null}
                </Stack>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'dbSNP',
          id: 'dbSNP',
          header: () => (
            <Stack maxWidth="100%">
              <Stack maxWidth="100%">
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Position')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('dbSNP')}
                </span>
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  sx={{
                    cursor: 'pointer',
                    color:
                      bgcolor === 'rgba(229,38,35,0.8)'
                        ? 'white'
                        : bgcolor === 'rgb(107,148,200,0.8)' ||
                          bgcolor === 'rgb(109,56,149,0.8)'
                        ? 'black'
                        : '#23527c',
                    fontWeight: '700'
                  }}
                  onClick={(e) => {
                    if (
                      (props.analysisInfo && props.analysisInfo.bamFile) ||
                      props.analysisInfo.sampleType === 'fastq'
                    ) {
                      props.handleMenuClick(e, 'IGV')

                      props.setCurrent && props.setCurrent(row.original)
                    } else if (
                      props.analysisInfo.analysisType === 'family_analysis' ||
                      props.analysisInfo.analysisType === 'big_family' ||
                      props.analysisInfo.analysisType === 'carrier'
                    ) {
                      const fqSamples = props.analysisInfo.samples?.filter(
                        (item: any) => item && item.sampleType === 'fastq'
                      )

                      if (fqSamples && fqSamples.length) {
                        props.handleMenuClick(e, 'IGV')

                        props.setCurrent && props.setCurrent(row.original)
                      } else {
                        window.open(
                          `${ExternalLinks.genome}?db=${
                            props.analysisInfo.genomeBuild ?? 'hg19'
                          }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                            row.original.chrom
                          }:${row.original.pos}-${row.original.pos}`
                        )
                      }
                    } else
                      window.open(
                        `${ExternalLinks.genome}?db=${
                          props.analysisInfo.genomeBuild ?? 'hg19'
                        }&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${
                          row.original.chrom
                        }:${row.original.pos}-${row.original.pos}`
                      )
                  }}
                >
                  {`${row.original.chrom}:${row.original.pos}`}
                </TypographyA>

                <Tooltip
                  disableHoverListener={
                    row.original.rsid ? row.original.rsid?.length <= 14 : true
                  }
                  title={row.original.rsid}
                  placement={'top-start'}
                >
                  <TypographyA
                    sx={{
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)'
                          ? 'white'
                          : bgcolor === 'rgb(107,148,200,0.8)' ||
                            bgcolor === 'rgb(109,56,149,0.8)'
                          ? 'black'
                          : '#23527c',
                      cursor: 'pointer',
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      fontWeight: '700'
                    }}
                    onClick={() => {
                      window.open(
                        `${ExternalLinks.ncbi.snp_ref}?type=rs&rs=${row.original.rsid}`
                      )
                    }}
                  >
                    {`${row.original.rsid ?? ''}`}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          }
        },
        {
          accessorKey: 'coding_effect',
          id: 'coding_effect',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Function')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('Location')}`}
                </span>
              </Stack>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => (
            <Stack maxWidth={'100%'} direction="column" spacing={1}>
              <Tooltip
                disableHoverListener={
                  row.original.consequence
                    ? row.original.consequence?.length <= 14
                    : true
                }
                placement={'top-start'}
                title={row.original.consequence}
              >
                <TypographyA
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.consequence}`}</TypographyA>
              </Tooltip>

              <Tooltip
                disableHoverListener={
                  row.original.variant_locations
                    ? row.original.variant_locations?.join(';')?.length +
                        row.original.exon?.length <=
                      14
                    : true
                }
                placement={'top-start'}
                title={`${row.original.variant_locations?.join(';')}${
                  row.original.exon === '.' || !row.original.exon
                    ? ''
                    : `(${row.original.exon})`
                }`}
              >
                <TypographyA
                  sx={{
                    maxWidth: 100,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >{`${row.original.variant_locations?.join(';')}${
                  row.original.exon === '.' || !row.original.exon
                    ? ''
                    : `(${row.original.exon})`
                }`}</TypographyA>
              </Tooltip>
            </Stack>
          )
        },
        {
          accessorKey: 'nuc_change',
          id: 'nuc_change',
          enableSorting: false,
          header: () => (
            <React.Fragment>
              <Stack maxWidth="100%">
                <span
                  style={{
                    textAlign: 'start'
                  }}
                >
                  {t('Base change')}
                </span>
              </Stack>
            </React.Fragment>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            const sampleList = props.analysisInfo.samples ?? []
            const flag =
              sampleList?.some(
                (sample: any) =>
                  (sample.cnvBatchTaskId &&
                    sample.cnvBatchStatus === 'processed') ||
                  sample.hasExomeDepthCnv
              ) && row.original.CNV

            return (
              <Stack
                maxWidth={'100%'}
                direction="row"
                spacing={1}
                justifyContent="space-between"
                alignItems={'center'}
              >
                <Box>
                  <Stack
                    direction="row"
                    alignItems={'center'}
                    justifyContent="start"
                    width={'100%'}
                  >
                    <Tooltip
                      disableHoverListener={
                        `${row.original.ref}>${row.original.alt}`?.length <= 14
                      }
                      title={`${row.original.ref}>${row.original.alt}`}
                      placement="top-start"
                    >
                      <TypographyA
                        sx={{
                          maxWidth: 140,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          color: '#23527c',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                        onClick={(e) => {
                          props.setCurrent && props.setCurrent(row.original)

                          props.setFilterGeneOpen(true)

                          props.setMenuType('filter_gene')

                          props.setFilterGeneData([])

                          props.getFilterGene.mutateAsync(row.original.gene)

                          e.stopPropagation()

                          e.preventDefault()
                        }}
                      >
                        {`${row.original.ref}>${row.original.alt}`}
                      </TypographyA>
                    </Tooltip>

                    {flag ? (
                      <TypographyA
                        sx={{
                          marginLeft: '15px',
                          color: 'red !important'
                        }}
                      >
                        cnv
                      </TypographyA>
                    ) : (
                      ''
                    )}
                  </Stack>

                  <Tooltip
                    disableHoverListener={
                      row.original.hgvsc
                        ? row.original.hgvsc?.length <= 14
                        : true
                    }
                    title={`${row.original.hgvsc}`}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        textAlign: 'left'
                      }}
                    >
                      {`${row.original.hgvsc ?? ''}`}
                    </TypographyA>
                  </Tooltip>

                  <Tooltip
                    disableHoverListener={
                      row.original.hgvsp
                        ? row.original.hgvsp?.length <= 14
                        : true
                    }
                    title={`${row.original.hgvsp}`}
                    placement="top-start"
                  >
                    <TypographyA
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        textAlign: 'left'
                      }}
                    >
                      {`${row.original.hgvsp ?? ''}`}
                    </TypographyA>
                  </Tooltip>
                </Box>
              </Stack>
            )
          }
        },
        {
          accessorKey: 'ad',
          id: 'ad',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip
                placement="top-start"
                title={`实心圆表纯合，半实心表杂合；ref/alt；新发变异紫色加粗`}
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Zygosity')}/${t('Coverage')}`}
                  </span>

                  {type === 'sample' ? (
                    <React.Fragment>
                      <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                        {t('QUAL')}
                      </span>
                    </React.Fragment>
                  ) : (
                    ''
                  )}
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {type === 'sample' ? (
                  <React.Fragment>
                    {(row.original.allele_balance ||
                      row.original.allele_balance === 0) &&
                    row.original.allele_balance !== '.' ? (
                      <Stack direction={'row'} alignItems="center" spacing={1}>
                        <Stack maxWidth={'100%'} direction="column" spacing={1}>
                          {row.original.allele_balance < 0.85 ? (
                            <CircleHalfFull
                              sx={{ color: 'black', fontSize: '0.875rem' }}
                            />
                          ) : (
                            <Circle
                              sx={{ color: 'black', fontSize: '0.875rem' }}
                            />
                          )}
                        </Stack>

                        <TypographyA
                          sx={{
                            maxWidth: '100%'
                          }}
                        >
                          {`${
                            row.original.ad
                          }  [${row.original.allele_balance?.toFixed(2)}] `}
                        </TypographyA>
                      </Stack>
                    ) : (
                      ''
                    )}

                    <TypographyA
                      sx={{
                        maxWidth: '100%',
                        textAlign: 'left'
                      }}
                    >
                      {row.original.qual &&
                      (row.original.qual as unknown as string) !== '.'
                        ? `${row.original.qual?.toFixed(1)}`
                        : ''}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {row.original.group_data
                      ? row.original.group_data
                          .map((item: any) => {
                            const temp = renameChildTypes(
                              props.analysisInfo.samples
                            ).find((items: any) => items.id == item.sample_id)
                            const type = temp ? temp.type : ''

                            return {
                              ...item,
                              type: type,
                              typeIndex:
                                type === 'affected_child'
                                  ? 0
                                  : type === 'mother'
                                  ? 2
                                  : type === 'father'
                                  ? 1
                                  : type === 'child'
                                  ? 0
                                  : 0
                            }
                          })
                          .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
                          .map((item: any) => {
                            if (item && item.allele_balance && item.ad) {
                              const temp = item

                              return (
                                <Tooltip
                                  title={
                                    temp
                                      ? temp.type.includes('//')
                                        ? `${t(temp.type.split('//')[0])}${
                                            temp.type.split('//')[1]
                                          }`
                                        : t(temp.type)
                                      : ''
                                  }
                                  placement="top-end"
                                >
                                  <Stack
                                    direction={'row'}
                                    alignItems="center"
                                    key={item.sample_id}
                                    spacing={1}
                                  >
                                    <Stack
                                      maxWidth={'100%'}
                                      direction="column"
                                      spacing={1}
                                    >
                                      {item.allele_balance < 0.85 ? (
                                        <CircleHalfFull
                                          sx={{
                                            color: 'black',
                                            fontSize: '0.875rem'
                                          }}
                                        />
                                      ) : (
                                        <Circle
                                          sx={{
                                            color: 'black',
                                            fontSize: '0.875rem'
                                          }}
                                        />
                                      )}
                                    </Stack>

                                    <TypographyA
                                      sx={{
                                        maxWidth: '100%'
                                      }}
                                    >
                                      {`${
                                        item.ad
                                      } [${item.allele_balance.toFixed(2)}] (${
                                        temp
                                          ? judgeType(
                                              temp.type,
                                              row.original.group_data
                                            )
                                          : ''
                                      })`}
                                    </TypographyA>
                                  </Stack>
                                </Tooltip>
                              )
                            }
                          })
                      : ''}
                  </React.Fragment>
                )}
              </Stack>
            )
          }
        },
        {
          accessorKey: 'index',
          id: 'index',
          enableSorting: false,
          header: () => (
            <Tooltip
              title={
                <>
                  <p>用户下有该基因的样本量/用户有该基因的样本量</p>

                  <p>组织下有该基因的样本量/组织有该基因的样本量</p>

                  <p>平台下有该基因的样本量/平台有该基因的样本量</p>
                </>
              }
            >
              <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
                <Stack maxWidth={'100%'}>
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('Variant Index (User)')}
                    </span>
                  </React.Fragment>
                </Stack>

                <Stack maxWidth={'100%'}>
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('Variant Index (Organization)')}
                    </span>
                  </React.Fragment>
                </Stack>

                <Stack maxWidth={'100%'}>
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('Variant Index (GLS)')}
                    </span>
                  </React.Fragment>
                </Stack>
              </Stack>
            </Tooltip>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            const tempGlsIndex = row.original.tempGlsIndex

            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack
                onClick={() => {
                  props.setCurrent(row.original)

                  props.setIndexOpen(true)
                }}
                maxWidth={'100%'}
                direction="column"
                spacing={1}
              >
                {tempGlsIndex ? (
                  <React.Fragment>
                    <TypographyA
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : '#23527c'
                      }}
                    >
                      {`${tempGlsIndex.user_index ?? 0} / ${
                        tempGlsIndex.user ?? 0
                      }`}
                    </TypographyA>

                    <TypographyA
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : '#23527c'
                      }}
                    >
                      {`${tempGlsIndex.organization_index ?? 0} / ${
                        tempGlsIndex.organization ?? 0
                      }`}
                    </TypographyA>

                    <TypographyA
                      sx={{
                        width: '100%',
                        maxWidth: 140,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : '#23527c'
                      }}
                    >
                      {`${tempGlsIndex.platform_index ?? 0} / ${
                        tempGlsIndex.platform ?? 0
                      }`}
                    </TypographyA>
                  </React.Fragment>
                ) : (
                  ''
                )}
              </Stack>
            )
          }
        },
        {
          accessorKey: 'articles',
          id: 'articles',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <React.Fragment>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Articles')}
                  </span>
                </React.Fragment>
              </Stack>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            const LitVarArticles = row.original.LitVarArticles
            const Articles = row.original.Articles
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack
                onClick={(e) => {
                  //
                  e.stopPropagation()

                  props.setAcmgOpen(true)

                  props.setCurrent(row.original)

                  setTimeout(() => {
                    props.VariationGeneRef.current &&
                      props.VariationGeneRef.current.handleClickLeft &&
                      props.VariationGeneRef.current.handleClickLeft(
                        3,
                        'Publication'
                      )
                  }, 2000)
                }}
                maxWidth={'100%'}
                direction="column"
                spacing={1}
                sx={{
                  cursor: 'pointer'
                }}
              >
                {LitVarArticles?.count ? (
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    LitVar:{LitVarArticles?.count ?? 0}
                  </TypographyA>
                ) : (
                  ''
                )}

                {Articles?.variantPubmedArticlesCount ? (
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    Clinvar:{Articles?.variantPubmedArticlesCount ?? 0}
                  </TypographyA>
                ) : (
                  ''
                )}

                {Articles?.genePubmedArticlesCount ? (
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 140,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      color:
                        bgcolor === 'rgba(229,38,35,0.8)' ? 'white' : '#23527c'
                    }}
                  >
                    OMIM:{Articles?.genePubmedArticlesCount ?? 0}
                  </TypographyA>
                ) : (
                  ''
                )}
              </Stack>
            )
          }
        },
        {
          accessorKey: 'gnomAD_exome_ALL',
          id: 'gnomAD_exome_ALL',
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              {/* <Filters keys="gnomAD_exome_ALL" /> */}

              <Stack maxWidth={'100%'} justifyContent="center">
                <>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`GnomAD Exome ALL`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`GnomAD Genome ALL`}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('WBBC')}`}
                  </span>
                </>
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => (
            <Stack maxWidth={'100%'} direction="column">
              <>
                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {(row.original.gnomade_af as unknown as string) === '.'
                    ? '.'
                    : row.original.gnomade_af === 0
                    ? 0
                    : `${formatScientificToDecimal(row.original.gnomade_af)}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {(row.original.gnomadg_af as unknown as string) === '.'
                    ? '.'
                    : row.original.gnomadg_af === 0
                    ? 0
                    : `${formatScientificToDecimal(row.original.gnomadg_af)}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: '100%'
                  }}
                >
                  {(row.original.wbbc_af as unknown as string) === '.'
                    ? '.'
                    : row.original.wbbc_af === 0
                    ? 0
                    : `${formatScientificToDecimal(row.original.wbbc_af)}`}
                </TypographyA>
              </>
            </Stack>
          )
        },
        {
          accessorKey: 'mitomap_freq',
          id: 'mitomap_freq',
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('mitomap_freq')}`}
                </span>
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => (
            <Stack maxWidth={'100%'} direction="column">
              <TypographyA
                sx={{
                  maxWidth: '100%'
                }}
              >
                {(row.original.mitomap_freq as unknown as string) === '.'
                  ? '.'
                  : row.original.mitomap_freq === 0
                  ? 0
                  : `${Number(row.original.mitomap_freq)?.toFixed(4)}`}
              </TypographyA>
            </Stack>
          )
        },
        {
          accessorKey: 'f_score',
          id: 'F_score',
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              {/* <Filters keys="f_score" /> */}

              <Tooltip
                placement="top-start"
                title="F score:GLSofort 软件AI模型综合评分：致病性、质量、表型、遗传模式、文献等等，最可能的致病位点"
              >
                <Stack maxWidth={'100%'}>
                  <span
                    style={{
                      lineHeight: 'normal',
                      textAlign: 'left'
                    }}
                  >
                    {t('F')}
                  </span>

                  <span
                    style={{
                      lineHeight: 'normal',
                      textAlign: 'left'
                    }}
                  >
                    Score
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'}>
                <Stack maxWidth={'100%'} direction="column" spacing={1}>
                  {type === 'sample' ? (
                    <React.Fragment>
                      <TypographyA
                        onClick={(e) => {
                          e.stopPropagation()

                          props.setGeneOpen(true)

                          props.setCurrent(row.original)
                        }}
                        sx={{
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          color:
                            bgcolor === 'rgba(229,38,35,0.8)'
                              ? 'white'
                              : bgcolor === 'rgb(107,148,200,0.8)' ||
                                bgcolor === 'rgb(109,56,149,0.8)'
                              ? 'black'
                              : '#23527c',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.f_score as any) !== '.'
                            ? (row.original.f_score * 100)?.toFixed(2)
                            : '.'
                        }`}
                      </TypographyA>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      {row.original.group_data
                        ? row.original.group_data
                            .map((item) => {
                              const temp = renameChildTypes(
                                props.analysisInfo.samples
                              ).find((items: any) => items.id == item.sample_id)
                              const type = temp ? temp.type : ''

                              return {
                                ...item,
                                type: type,
                                typeIndex:
                                  type === 'affected_child'
                                    ? 0
                                    : type === 'mother'
                                    ? 2
                                    : type === 'father'
                                    ? 1
                                    : type === 'child'
                                    ? 0
                                    : 0
                              }
                            })
                            .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
                            .map((item: any) => {
                              if (item && item.f_score) {
                                const temp = item

                                return (
                                  <Tooltip
                                    title={
                                      temp
                                        ? temp.type.includes('//')
                                          ? `${t(temp.type.split('//')[0])}${
                                              temp.type.split('//')[1]
                                            }`
                                          : t(temp.type)
                                        : ''
                                    }
                                    placement="top-end"
                                  >
                                    <TypographyA
                                      onClick={(e) => {
                                        e.stopPropagation()

                                        props.setGeneOpen(true)

                                        props.setCurrent(row.original)
                                      }}
                                      sx={{
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        color:
                                          bgcolor === 'rgba(229,38,35,0.8)'
                                            ? 'white'
                                            : bgcolor ===
                                                'rgb(107,148,200,0.8)' ||
                                              bgcolor === 'rgb(109,56,149,0.8)'
                                            ? 'black'
                                            : '#23527c',
                                        textAlign: 'center',
                                        fontWeight: '700'
                                      }}
                                    >
                                      {`${
                                        (item.f_score as any) !== '.'
                                          ? `${(item.f_score * 100)?.toFixed(
                                              2
                                            )}(${
                                              temp
                                                ? judgeType(
                                                    temp.type,
                                                    row.original.group_data
                                                  )
                                                : ''
                                            })`
                                          : '.'
                                      }`}
                                    </TypographyA>
                                  </Tooltip>
                                )
                              }
                            })
                        : ''}
                    </React.Fragment>
                  )}
                </Stack>
              </Stack>
            )
          }
        },
        {
          accessorKey: 'g_score',
          id: 'G_score',
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              {/* <Filters keys="g_score" /> */}

              <Tooltip
                placement="top-start"
                title={t(
                  'G score: Phenotypic correlation score, which is a comprehensive score associated with genetic diseases and other genetic diseases that interact with genes'
                )}
              >
                <Stack maxWidth={'100%'}>
                  <span
                    style={{
                      lineHeight: 'normal',
                      textAlign: 'left'
                    }}
                  >
                    {t('G')}
                  </span>

                  <span
                    style={{
                      lineHeight: 'normal',
                      textAlign: 'left'
                    }}
                  >
                    Score
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            return (
              <Stack maxWidth={'100%'}>
                <Stack maxWidth={'100%'} direction="column" spacing={1}>
                  {type === 'sample' ? (
                    <React.Fragment>
                      <TypographyA
                        sx={{
                          fontSize: '0.8rem',
                          textAlign: 'center',
                          fontWeight: '700'
                        }}
                      >
                        {`${
                          (row.original.g_score as any) !== '.'
                            ? (row.original.g_score * 100)?.toFixed(2)
                            : '.'
                        }`}
                      </TypographyA>
                    </React.Fragment>
                  ) : (
                    <React.Fragment>
                      {row.original.group_data
                        ? row.original.group_data
                            .map((item) => {
                              const temp = renameChildTypes(
                                props.analysisInfo.samples
                              ).find((items: any) => items.id == item.sample_id)
                              const type = temp ? temp.type : ''

                              return {
                                ...item,
                                type: type,
                                typeIndex:
                                  type === 'affected_child'
                                    ? 0
                                    : type === 'mother'
                                    ? 2
                                    : type === 'father'
                                    ? 1
                                    : type === 'child'
                                    ? 0
                                    : 0
                              }
                            })
                            .sort((a: any, b: any) => a.typeIndex - b.typeIndex)
                            .map((item: any) => {
                              if (item && (item.g_score as any) !== '.') {
                                const temp = item

                                return (
                                  <Tooltip
                                    title={
                                      temp
                                        ? temp.type.includes('//')
                                          ? `${t(temp.type.split('//')[0])}${
                                              temp.type.split('//')[1]
                                            }`
                                          : t(temp.type)
                                        : ''
                                    }
                                    placement="top-end"
                                  >
                                    <TypographyA
                                      onClick={(e) => {
                                        e.stopPropagation()

                                        props.setGeneOpen(true)

                                        props.setCurrent(row.original)
                                      }}
                                      sx={{
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        textAlign: 'center',
                                        fontWeight: '700'
                                      }}
                                    >
                                      {`${
                                        (item.g_score as any) !== '.'
                                          ? `${(item.g_score * 100)?.toFixed(
                                              2
                                            )}(${
                                              temp
                                                ? judgeType(
                                                    temp.type,
                                                    row.original.group_data
                                                  )
                                                : ''
                                            })`
                                          : '.'
                                      }`}
                                    </TypographyA>
                                  </Tooltip>
                                )
                              }
                            })
                        : ''}
                    </React.Fragment>
                  )}
                </Stack>
              </Stack>
            )
          }
        },
        {
          accessorKey: 'SpliceAI_max_score',
          id: 'spliceAI_max_score',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} alignItems="center">
              <Tooltip
                title={`D(${t('harmful')}) U(${t(
                  'unknown significance'
                )}) B(${t('benign')})`}
                placement="top-start"
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('SpliceAI')}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Revel')}
                  </span>

                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('AlphaM')}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            return (
              <Stack maxWidth={'100%'}>
                <Stack
                  maxWidth={'100%'}
                  direction="column"
                  spacing={1}
                  justifyContent="center"
                  alignItems="center"
                >
                  <TypographyA
                    sx={{
                      fontSize: '0.8rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pr: '10px'
                    }}
                  >
                    <span>
                      {(row.original
                        .spliceai_max_score as unknown as string) === '.'
                        ? '.'
                        : `${Number(row.original.spliceai_max_score)?.toFixed(
                            2
                          )}`}
                    </span>

                    <span
                      style={{
                        fontWeight: row.original.spliceai_max_score
                          ? row.original.spliceai_max_score >= 0.2
                            ? 'bold'
                            : 'normal'
                          : 'normal'
                      }}
                    >
                      {`${
                        row.original.spliceai_max_score
                          ? row.original.spliceai_max_score >= 0.2
                            ? 'D'
                            : row.original.spliceai_max_score <= 0.1
                            ? ''
                            : ''
                          : ''
                      }`}
                    </span>
                  </TypographyA>

                  <TypographyA
                    sx={{
                      fontSize: '0.8rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pr: '10px'
                    }}
                  >
                    <span>
                      {(row.original.revel as unknown as string) === '.'
                        ? '.'
                        : `${Number(row.original.revel)?.toFixed(3)}`}
                    </span>

                    <span
                      style={{
                        fontWeight: row.original.revel
                          ? row.original.revel >= 0.644
                            ? 'bold'
                            : 'normal'
                          : 'normal'
                      }}
                    >
                      {`${
                        row.original.revel
                          ? row.original.revel >= 0.644
                            ? 'D'
                            : row.original.revel <= 0.29
                            ? ''
                            : ''
                          : ''
                      }`}
                    </span>
                  </TypographyA>

                  <TypographyA
                    sx={{
                      fontSize: '0.8rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      pr: '10px'
                    }}
                  >
                    <span>
                      {(row.original.am_pathogenicity as unknown as string) ===
                        '.' || !row.original.am_pathogenicity
                        ? '.'
                        : `${Number(row.original.am_pathogenicity)?.toFixed(
                            2
                          )}`}
                    </span>

                    <span
                      style={{
                        fontWeight:
                          Number(row.original.am_pathogenicity) >= 0.564
                            ? 'bold'
                            : 'normal'
                      }}
                    >
                      {`${
                        Number(row.original.am_pathogenicity) >= 0.564
                          ? 'D'
                          : Number(row.original.am_pathogenicity) <= 0.34
                          ? ''
                          : ''
                      }`}
                    </span>
                  </TypographyA>
                </Stack>
              </Stack>
            )
          }
        },

        // {
        //   accessorKey: 'Revel_score',
        //   id: 'revel_score',
        //   header: () => (
        //     <React.Fragment>
        //       <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //         <span>{t('Revel')}</span>
        //       </Stack>
        //     </React.Fragment>
        //   ),
        //   footer: (props) => props.column.id,
        //   cell: ({ row, getValue }) => {
        //     return (
        //       <Stack maxWidth={'100%'}>
        //         <TypographyA
        //           sx={{
        //             maxWidth: '100%',
        //             textAlign: 'center'
        //           }}
        //         >
        //           {(row.original.REVEL_score as unknown as string) === '.'
        //             ? '.'
        //             : `${row.original.REVEL_score?.toFixed(3)}`}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   }
        // },
        // {
        //   accessorKey: 'pathogenicity',
        //   id: 'Pathogenicity',
        //   header: () => (
        //     <React.Fragment>
        //       <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
        //         <span>{t('AlphaM')}</span>
        //       </Stack>
        //     </React.Fragment>
        //   ),
        //   footer: (props) => props.column.id,
        //   cell: ({ row, getValue }) => {
        //     return (
        //       <Stack maxWidth={'100%'}>
        //         <TypographyA
        //           sx={{
        //             maxWidth: '100%',
        //             textAlign: 'center'
        //           }}
        //         >
        //           {(row.original.AM_pathogenicity as unknown as string) === '.' ||
        //           !row.original.AM_pathogenicity
        //             ? '.'
        //             : `${Number(row.original.AM_pathogenicity)?.toFixed(2)}`}
        //         </TypographyA>
        //       </Stack>
        //     )
        //   }
        // },
        {
          accessorKey: 'CLINVAR_variant_id',
          id: 'cLINVAR_variant_id',
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              {/* <Filters keys="CLINVAR_clnsig_final" /> */}

              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Clinvar (star)')}
                </span>
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} direction={'column'} spacing={1}>
                <Stack
                  alignItems={'center'}
                  maxWidth={'100%'}
                  spacing={1}
                  direction="row"
                >
                  <Tooltip
                    title={row.original.clinvar_clinsig_final}
                    placement={'top-start'}
                  >
                    <TypographyA
                      sx={{
                        color:
                          bgcolor === 'rgba(229,38,35,0.8)'
                            ? 'white'
                            : '#23527c',
                        cursor: 'pointer',
                        maxWidth: 80,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                      }}
                      onClick={() => {
                        window.open(
                          `${ExternalLinks.ncbi.variation}/${row.original.clinvar_id}`
                        )
                      }}
                    >
                      {`${row.original.clinvar_clinsig_final ?? ''}`}
                    </TypographyA>
                  </Tooltip>

                  {row.original.clinvar_gold_stars &&
                  row.original.clinvar_gold_stars !== '.' ? (
                    <Rating
                      readOnly
                      value={
                        row.original.clinvar_gold_stars as unknown as number
                      }
                      max={row.original.clinvar_gold_stars as unknown as number}
                    />
                  ) : (
                    ''
                  )}
                </Stack>
              </Stack>
            )
          }
        },
        {
          accessorKey: 'ACMG_priority',
          id: 'ACMG_priority',
          header: () => (
            <React.Fragment>
              {/* <Filters keys="ACMG_classification" /> */}

              <Stack maxWidth={'100%'} direction={'column'} alignItems="center">
                <span>{t('ACMG')}</span>

                <span>{t('Classification')}</span>
              </Stack>
            </React.Fragment>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            const acmg = row.original.acmg_classification
              ? `${row.original.acmg_classification}`
              : row.original.user_acmg_classification
              ? `${row.original.user_acmg_classification}`
              : 'VUS'

            let bgcolor = color.find((i) => switchACMG(i.key) === acmg)
              ? (color.find((i) => switchACMG(i.key) === acmg)?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'none'
            if (row.original.user_acmg_classification) {
              bgcolor = color.find(
                (i) =>
                  i.key === row.original.user_acmg_classification?.toLowerCase()
              )
                ? (color.find(
                    (i) =>
                      i.key ===
                      row.original.user_acmg_classification?.toLowerCase()
                  )?.color as
                    | 'rgb(240,239,240,0.4)'
                    | 'rgba(221,109,32,0.8)'
                    | 'rgb(255,242,202)'
                    | 'rgb(245,182,193)')
                : 'rgba(0, 0, 0)'

              return (
                <Stack maxWidth={'100%'}>
                  <Box
                    sx={{
                      maxWidth: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Box>
                      <Chip
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          minWidth: '45px',
                          textAlign: 'center'
                        }}
                        label={`${row.original.user_acmg_classification
                          .split(' ')
                          .map((word: string) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0] && word[0].toUpperCase()
                          })
                          .join('')}${
                          row.original.ACMG_score ||
                          row.original.ACMG_score === 0
                            ? `:${row.original.ACMG_score}`
                            : ``
                        }`}
                        size="small"
                        onClick={() => {
                          props.setAcmgOpen(true)

                          props.setCurrent && props.setCurrent(row.original)
                        }}
                      />
                    </Box>

                    {/* <Box>
                      {row.original.user_classification ? (
                        <Chip
                          key={row.original.user_classification}
                          size="small"
                          label={`${row.original.user_classification
                            .split(' ')
                            .map((word: any) => {
                              if (word.includes('Uncertain')) return `VU`
                              if (word.includes('uncertain')) return `VU`
                              if (word.includes('null')) return ``
                              else return word[0] && word[0].toUpperCase()
                            })
                            .join('')}`}
                          sx={{
                            bgcolor: bgcolor,
                            color:
                              bgcolor === 'rgb(240,239,240,0.4)'
                                ? 'black'
                                : 'white',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            textAlign: 'center',
                            minWidth: '45px'
                          }}
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOpen(true)
                          }}
                        />
                      ) : (
                        <IconButton
                          aria-label="edit"
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOpen(true)
                          }}
                          sx={{
                            padding: 0
                          }}
                        >
                          <CommentOutline
                            sx={{
                              fontSize: '1rem'
                            }}
                          />
                        </IconButton>
                      )}
                    </Box> */}
                  </Box>
                </Stack>
              )
            } else if (
              acmg !== '.' ||
              row.original.user_acmg_classification !== '.'
            ) {
              return (
                <Stack maxWidth={'100%'}>
                  {/* <TypographyA
                  sx={{
                    maxWidth: '100%',
                    textAlign: 'center'
                  }}
                >
                  {`${row.original.ACMG_score}`}
                </TypographyA> */}

                  <Box
                    sx={{
                      maxWidth: '100%',
                      textAlign: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center'
                    }}
                  >
                    <Box>
                      <Chip
                        sx={{
                          bgcolor: bgcolor,
                          color:
                            bgcolor === 'rgb(240,239,240,0.4)'
                              ? 'black'
                              : 'white',
                          justifyContent: 'center',
                          fontSize: '0.75rem',
                          minWidth: '45px',
                          fontWeight: '700',
                          textAlign: 'center'
                        }}
                        label={`${acmg
                          ?.split(' ')
                          .map((word: string) => {
                            if (word.includes('Uncertain')) return `VU`
                            if (word.includes('null')) return ``
                            else return word[0] && word[0].toUpperCase()
                          })
                          .join('')}${
                          row.original.acmg_score ||
                          row.original.acmg_score === 0
                            ? `:${row.original.acmg_score}`
                            : ``
                        }`}
                        size="small"
                        onClick={() => {
                          props.setAcmgOpen(true)

                          props.setCurrent && props.setCurrent(row.original)
                        }}
                      />
                    </Box>

                    <Box>
                      {row.original.user_classification ? (
                        <Chip
                          key={row.original.user_classification}
                          size="small"
                          label={`${row.original.user_classification
                            .split(' ')
                            .map((word: any) => {
                              if (word.includes('Uncertain')) return `VU`
                              if (word.includes('uncertain')) return `VU`
                              if (word.includes('null')) return ``
                              else return word[0] && word[0].toUpperCase()
                            })
                            .join('')}`}
                          sx={{
                            bgcolor: bgcolor,
                            color:
                              bgcolor === 'rgb(240,239,240,0.4)'
                                ? 'black'
                                : 'white',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            textAlign: 'center',
                            minWidth: '45px'
                          }}
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOpen(true)
                          }}
                        />
                      ) : (
                        <IconButton
                          aria-label="edit"
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOpen(true)
                          }}
                          sx={{
                            padding: 0
                          }}
                        >
                          <CommentOutline
                            sx={{
                              fontSize: '1rem'
                            }}
                          />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Stack>
              )
            } else
              return (
                <>
                  <TypographyA
                    sx={{
                      maxWidth: '100%',
                      textAlign: 'center'
                    }}
                  >
                    {`${row.original.acmg_score}`}
                  </TypographyA>

                  <TypographyA textAlign={'center'}>.</TypographyA>
                </>
              )
          }
        },
        {
          accessorKey: 'inheritance',
          id: 'inheritance',
          header: () => (
            <Stack width={'100%'} direction={'row'} justifyContent="center">
              <Stack maxWidth={'100%'} justifyContent="center">
                {props.query && props.query.key && props.query.key === '5' ? (
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {t('Disease Information')}
                  </span>
                ) : (
                  <React.Fragment>
                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('OMIM')}
                    </span>

                    <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                      {t('Inheritance')}
                    </span>
                  </React.Fragment>
                )}
              </Stack>
            </Stack>
          ),
          enableSorting: false,
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            if (props.query && props.query.key && props.query.key === '5') {
              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {row.original.MitoMap && row.original.MitoMap.disease_ch ? (
                    <CustomWidthTooltip
                      title={
                        <Box>
                          <Typography color={'white'}>
                            {row.original.MitoMap.disease_ch}
                          </Typography>

                          <Typography color={'white'}>
                            {row.original.MitoMap.info}
                          </Typography>
                        </Box>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                    >
                      <Box>
                        <TypographyA
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            lineHeight: '24px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            mb: '4px'
                          }}
                          key={`${row.original.MitoMap.disease_ch}`}
                        >
                          {row.original.MitoMap.disease_ch}
                        </TypographyA>

                        <TypographyA
                          sx={{
                            width: '100%',
                            maxWidth: 200,
                            lineHeight: '24px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            cursor: 'pointer',
                            mb: '4px'
                          }}
                          key={`${row.original.MitoMap.info}`}
                        >
                          {row.original.MitoMap.info}
                        </TypographyA>
                      </Box>
                    </CustomWidthTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            } else if (
              i18n &&
              i18n.language === 'zh' &&
              row.original.omim_curate_phenotypes &&
              row.original.omim_curate_phenotypes.length
            ) {
              let OMIMValue = row.original.omim_curate_phenotypes

              OMIMValue = unique(OMIMValue, 'disease')

              let bgcolor = color.find(
                (i) => i.key === row.original.gls_classification?.toLowerCase()
              )
                ? (color.find(
                    (i) =>
                      i.key === row.original.gls_classification?.toLowerCase()
                  )?.color as any)
                : 'rgba(0, 0, 0)'

              // loss_of_function等于1 并且gls_classification不等于P/LP
              if (
                row.original.gls_classification?.toLowerCase() === 'pathogenic'
              ) {
                //
              } else if (
                row.original.gls_classification?.toLowerCase() ===
                'likely pathogenic'
              ) {
                //
              } else if (row.original.loss_of_function === 1)
                bgcolor = '#f5d18c'

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index: any) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.disease}_${index}`}
                            >
                              {`(${
                                item.genetic_mode ? item.genetic_mode : '.'
                              })-${
                                i18n && i18n.language === 'zh'
                                  ? item.disease
                                  : ''
                              }`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}

                      // enterDelay={1000}
                      // onMouseEnter={async (e) => {
                      //   const sourceLanguage = 'en',
                      //     targetLanguage = 'zh'
                      //   const params: TranslateDto = {
                      //     sourceLanguage,
                      //     targetLanguage,
                      //     sourceText: OMIMValue
                      //   }
                      //   Translate.mutateAsync(params)
                      // }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index: any) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color:
                                  bgcolor === 'rgb(109,56,149,0.8)' ||
                                  bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgba(229,38,35,0.8)'
                                    ? 'white'
                                    : '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent(row.original)

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.disease}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.genetic_mode ? items.genetic_mode : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${
                                i18n && i18n.language === 'zh'
                                  ? items.disease + '-'
                                  : ''
                              }${items.disease}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            } else {
              let OMIMValue =
                row.original?.omim_phenotypes?.map((item) => {
                  const inheritances: string[] = []
                  const clinicalSynopsis = isJson(item.clinicalSynopsis)
                    ? JSON.parse(item.clinicalSynopsis)
                    : item.clinicalSynopsis

                  if (
                    item &&
                    clinicalSynopsis &&
                    clinicalSynopsis.INHERITANCE &&
                    Array.isArray(clinicalSynopsis.INHERITANCE)
                  ) {
                    clinicalSynopsis.INHERITANCE.forEach((str: string) =>
                      inheritances.push(str.toLowerCase())
                    )
                  }
                  const inheritance = formatVariantInheritance(
                    Array.from([...new Set(inheritances)]).join(',')
                  )

                  return {
                    ...item,
                    inheritance
                  }
                }) ?? []

              OMIMValue = unique(OMIMValue, 'phenoName')

              let bgcolor = color.find(
                (i) => i.key === row.original.gls_classification?.toLowerCase()
              )
                ? (color.find(
                    (i) =>
                      i.key === row.original.gls_classification?.toLowerCase()
                  )?.color as any)
                : 'rgba(0, 0, 0)'

              // loss_of_function等于1 并且gls_classification不等于P/LP
              if (
                row.original.gls_classification?.toLowerCase() === 'pathogenic'
              ) {
                //
              } else if (
                row.original.gls_classification?.toLowerCase() ===
                'likely pathogenic'
              ) {
                //
              } else if (row.original.loss_of_function === 1)
                bgcolor = '#f5d18c'

              return (
                <Stack maxWidth={'100%'} spacing={1}>
                  {OMIMValue && OMIMValue.length ? (
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {OMIMValue.map((item: any, index) => (
                            <TypographyA
                              sx={{
                                fontSize: '0.85rem',
                                lineHeight: '1rem',
                                maxWidth: isWindow()
                                  ? '400px !important'
                                  : '200px !important'
                              }}
                              key={`${item.phenoName}_${index}`}
                            >
                              {`(${
                                item.inheritance ? item.inheritance : '.'
                              })-${
                                i18n && i18n.language === 'zh'
                                  ? item.pheno_name_cn + '-'
                                  : ''
                              }${item.phenoName}`}
                            </TypographyA>
                          ))}
                        </React.Fragment>
                      }
                      placement={'top-start'}
                      TransitionComponent={Slide}
                      sx={{
                        '& .MuiTooltip-tooltip': {
                          maxWidth: isWindow() ? '400px' : '200px'
                        }
                      }}

                      // enterDelay={1000}
                      // onMouseEnter={async (e) => {
                      //   const sourceLanguage = 'en',
                      //     targetLanguage = 'zh'
                      //   const params: TranslateDto = {
                      //     sourceLanguage,
                      //     targetLanguage,
                      //     sourceText: OMIMValue
                      //   }
                      //   Translate.mutateAsync(params)
                      // }}
                    >
                      <Box>
                        {OMIMValue.slice(0, 2).map((items: any, index) => {
                          return (
                            <TypographyA
                              sx={{
                                width: '100%',
                                maxWidth: 200,
                                lineHeight: '24px',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                                color:
                                  bgcolor === 'rgb(109,56,149,0.8)' ||
                                  bgcolor === 'rgb(107,148,200,0.8)' ||
                                  bgcolor === 'rgba(229,38,35,0.8)'
                                    ? 'white'
                                    : '#23527c',
                                mb: '4px'
                              }}
                              onClick={() => {
                                props.setCurrent(row.original)

                                props.setOMIMOpen(true)
                              }}
                              key={`${items.phenoMim}_${index}`}
                            >
                              <Chip
                                label={`${
                                  items.inheritance ? items.inheritance : '.'
                                }`}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: '0.75rem',
                                  fontWeight: '700'
                                }}
                              />

                              <span
                                style={{
                                  lineHeight: '24px'
                                }}
                              >{`${
                                i18n && i18n.language === 'zh'
                                  ? items.pheno_name_cn + '-'
                                  : ''
                              }${items.phenoName}`}</span>
                            </TypographyA>
                          )
                        })}

                        {/* {OMIMValue.length > 3 ? '...' : ''} */}
                      </Box>
                    </HtmlTooltip>
                  ) : (
                    <TypographyA>.</TypographyA>
                  )}
                </Stack>
              )
            }
          }
        },
        {
          accessorKey: 'status',
          id: 'status ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`mitomap ${t('status')}`}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <HtmlTooltip
                  placement={'top-start'}
                  TransitionComponent={Slide}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: isWindow() ? '400px' : '200px'
                    }
                  }}
                  title={
                    row.original.MitoMap ? row.original.MitoMap?.status : ''
                  }
                >
                  <TypographyA
                    sx={{
                      width: '100%',
                      maxWidth: 200,
                      lineHeight: '24px',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                      color: '#23527c',
                      mb: '4px'
                    }}
                  >
                    {row.original.MitoMap ? row.original.MitoMap?.status : ''}
                  </TypographyA>
                </HtmlTooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        }
      ]
    }

    if (!props.indexChecked) {
      column = column.filter((i) => i.id !== 'index')
    }

    return column
  }, [props, user])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1MT
